/**
 * Integrytas Group - User-Defined Column Engine (UDC)
 * use_udc_fields - runtime screen-hydration hook.
 *
 * Given a target table, fetches the active-workspace custom-field set (each field
 * already merged with its DSE definition + best-match UDC layout, ready to render).
 * A screen calls this on load and hands the result to <UdcCustomFields>.
 */

import { useCallback, useEffect, useState } from 'react';
import { apiDb } from '../lib/api_client';
import { asfWorkspaceCode } from '../lib/asf';

export type UdcControl =
  | 'TEXT' | 'TEXTAREA' | 'NUMBER' | 'DATE' | 'DATETIME' | 'CHECKBOX' | 'SELECT' | 'RADIO' | 'MODAL_LOOKUP';

export interface UdcField {
  key: string;
  label: string;
  data_type: 'STRING' | 'INT' | 'NUMBER' | 'BOOL' | 'DATE' | 'DATETIME' | 'ENUM';
  required: boolean;
  allowed_values: string[];
  format_name: string | null;
  regex: string | null;
  min_value: number | null;
  max_value: number | null;
  min_len: number | null;
  max_len: number | null;
  tab_code: string | null;
  section_code: string | null;
  display_order: number;
  col_span: number;
  control: UdcControl;
  placeholder: string | null;
  help_text: string | null;
}

export interface UseUdcFields {
  fields: UdcField[];
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
}

export function useUdcFields(
  entity: string | null | undefined,
  opts?: { workspaceCode?: string | null; pageCode?: string; enabled?: boolean },
): UseUdcFields {
  const [fields, setFields] = useState<UdcField[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const enabled = opts?.enabled ?? true;
  const pageCode = opts?.pageCode ?? '';

  const reload = useCallback(async () => {
    if (!entity || !enabled) { setFields([]); return; }
    setLoading(true); setError(null);
    try {
      const ws = opts?.workspaceCode !== undefined ? (opts.workspaceCode ?? '') : (asfWorkspaceCode() || '');
      // page_code scopes the field set to fields the admin placed on THIS page.
      const { data, error: e } = await apiDb.rpc<UdcField[]>('udc_screen_fields', { p_entity: entity, p_workspace_code: ws, p_page_code: pageCode });
      if (e) throw e;
      setFields(Array.isArray(data) ? data : []);
    } catch (err) {
      setError((err as Error).message); setFields([]);
    } finally {
      setLoading(false);
    }
  }, [entity, enabled, pageCode, opts?.workspaceCode]);

  useEffect(() => { void reload(); }, [reload]);

  return { fields, loading, error, reload };
}
